import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  FolderOpen,
  ImagePlus,
  Layers3,
  Loader2,
  Lock,
  Search,
  Settings2,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAssets } from '../../../hooks/useSupabase';
import { SceneScaperMockup, sceneScaperService } from '../../../services/sceneScaperService';
import { downloadService } from '../../../services/downloadService';
import { useNotification } from '../../../contexts/NotificationContext';
import ImagePreviewModal from '../../../components/ImagePreviewModal';
import { API_BASE_URL } from '../../../utils/apiUrl';
import { hasProfessionalAccess } from '../../../utils/licensing';

const STYLE_OPTIONS = [
  { id: 'photorealistic', label: 'Photorealistic', value: 'photorealistic' },
  { id: 'editorial', label: 'Editorial', value: 'editorial commercial photography' },
  { id: 'minimalist', label: 'Minimalist', value: 'minimalist premium styling' },
  { id: 'cinematic', label: 'Cinematic', value: 'cinematic lighting' },
  { id: 'vibrant', label: 'Vibrant', value: 'vibrant and colorful' },
  { id: 'moody', label: 'Moody', value: 'dark and moody' },
];

const PROMPT_STARTERS = [
  'Create a premium hero shot on a clean retail shelf with strong product focus.',
  'Build a lifestyle scene on a modern desk setup with natural morning light.',
  'Show the asset in a polished packaging close-up with tactile materials.',
  'Create an outdoor campaign shot with confident framing and believable shadows.',
  'Design a social-ready flat lay with layered props and balanced negative space.',
];

const ASPECT_OPTIONS = [
  { value: '1:1', label: 'Square', hint: 'Feeds and product grids' },
  { value: '4:5', label: 'Portrait', hint: 'Social posts and ads' },
  { value: '16:9', label: 'Landscape', hint: 'Hero banners and web' },
];

const formatBytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'scenescaper-mockup';

const resolveSceneImageUrl = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://') ? url : `${API_BASE_URL}${url}`;

const SceneScaper: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { assets, loading: assetsLoading } = useAssets();
  const { showNotification } = useNotification();
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [primaryPrompt, setPrimaryPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assetSearch, setAssetSearch] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>(['photorealistic']);
  const [variationsCount, setVariationsCount] = useState(2);
  const [generatedMockups, setGeneratedMockups] = useState<SceneScaperMockup[]>([]);
  const [previewMockup, setPreviewMockup] = useState<SceneScaperMockup | null>(null);
  const [downloadingMockupId, setDownloadingMockupId] = useState<string | null>(null);
  const [creditsBalance, setCreditsBalance] = useState(profile?.credits_balance || 0);

  const hasProfessionalLicense = hasProfessionalAccess(profile);

  useEffect(() => {
    setCreditsBalance(profile?.credits_balance || 0);
  }, [profile?.credits_balance]);

  const selectedAssets = useMemo(
    () => selectedAssetIds.map((id) => assets.find((asset) => asset.id === id)).filter(Boolean),
    [assets, selectedAssetIds]
  );

  const filteredAssets = useMemo(() => {
    const searchValue = assetSearch.trim().toLowerCase();
    if (!searchValue) return assets;
    return assets.filter((asset) =>
      asset.original_name.toLowerCase().includes(searchValue) || asset.file_type.toLowerCase().includes(searchValue)
    );
  }, [assetSearch, assets]);

  const generationCost = variationsCount * 2;
  const hasEnoughCredits = creditsBalance >= generationCost;

  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssetIds((current) =>
      current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]
    );
  };

  const toggleStyle = (styleValue: string) => {
    setSelectedStyles((current) =>
      current.includes(styleValue) ? current.filter((value) => value !== styleValue) : [...current, styleValue]
    );
  };

  const applyPromptStarter = (starter: string) => {
    setPrimaryPrompt((current) => (current.trim() ? `${current.trim()} ${starter}` : starter));
  };

  const handleGenerate = async () => {
    if (selectedAssetIds.length === 0) {
      setError('Select at least one asset to build your scene.');
      return;
    }
    if (!primaryPrompt.trim()) {
      setError('Describe the scene you want to generate.');
      return;
    }
    if (!hasEnoughCredits) {
      setError(`You need ${generationCost} credits to generate ${variationsCount} variation${variationsCount > 1 ? 's' : ''}.`);
      return;
    }

    setError(null);
    setGenerating(true);

    try {
      const result = await sceneScaperService.generateScene({
        assetIds: selectedAssetIds,
        primaryPrompt: primaryPrompt.trim(),
        styleModifiers: selectedStyles,
        negativePrompt: negativePrompt.trim() || undefined,
        aspectRatio,
        variationsCount,
      });

      setGeneratedMockups(result.mockups || []);
      setCreditsBalance(result.creditsRemaining);
      showNotification(
        'success',
        result.mockups.length > 1 ? 'Scene variations ready' : 'Scene ready',
        result.message || `Generated ${result.mockups.length} mockup variation${result.mockups.length > 1 ? 's' : ''}.`
      );
      window.setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      setError(err.message || 'Failed to generate scene. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (mockup: SceneScaperMockup) => {
    setDownloadingMockupId(mockup.mockupId);
    setError(null);
    try {
      await downloadService.downloadMockup(
        mockup.mockupId,
        `${slugify(mockup.name)}.png`,
        'png',
        'web',
        mockup.mockupUrl
      );
    } catch (err: any) {
      setError(err.message || 'Failed to download generated mockup.');
    } finally {
      setDownloadingMockupId(null);
    }
  };

  const selectedAspect = ASPECT_OPTIONS.find((option) => option.value === aspectRatio);

  if (!hasProfessionalLicense) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-10">
        <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 to-teal-700">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-slate-900">SceneScaper AI is a Pro feature</h2>
          <p className="mb-8 text-sm leading-6 text-slate-600">
            Upgrade to Professional or Agency to combine multiple assets, generate scene variations, and export polished mockups directly from this workspace.
          </p>
          <button
            onClick={() => navigate('/dashboard/settings?upgrade=pro')}
            className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
          >
            Upgrade License
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-950 via-slate-900 to-teal-900 text-white shadow-2xl">
          <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.35fr_0.65fr] lg:px-8">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-teal-100">
                <Sparkles className="h-3.5 w-3.5" />
                SceneScaper AI
              </div>
              <h1 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
                Build polished, brand-ready scenes without leaving your dashboard.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
                Combine multiple image assets, guide the creative direction, and generate up to four mockup variations in one run. Every result is saved to your Library automatically.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2">Credits: <span className="font-semibold">{creditsBalance}</span></div>
                <div className="rounded-full border border-teal-300/20 bg-teal-400/10 px-4 py-2 text-teal-100">Run cost: <span className="font-semibold text-white">{generationCost}</span></div>
                <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2">Best with 1-4 hero assets</div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Assets</p>
                <p className="mt-3 text-3xl font-bold">{selectedAssetIds.length}</p>
                <p className="mt-1 text-sm text-slate-300">Mix logos, packaging, product renders, or campaign elements.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Variations</p>
                <p className="mt-3 text-3xl font-bold">{variationsCount}</p>
                <p className="mt-1 text-sm text-slate-300">Generate multiple compositions in a single pass.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Ratio</p>
                <p className="mt-3 text-3xl font-bold">{selectedAspect?.label || 'Square'}</p>
                <p className="mt-1 text-sm text-slate-300">{selectedAspect?.hint || 'Feeds and product grids'}</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.45fr_0.55fr]">
          <section className="space-y-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Step 1</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Choose the assets to place in your scene</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">Select one or more images from your Brand Kit. SceneScaper will preserve these visuals and place them into the generated composition.</p>
                </div>
                <button
                  onClick={() => navigate('/dashboard/assets')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <ImagePlus className="h-4 w-4" />
                  Manage assets
                </button>
              </div>

              {selectedAssets.length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2 rounded-2xl border border-teal-100 bg-teal-50/70 p-3">
                  {selectedAssets.map((asset) => (
                    <span key={asset!.id} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm">
                      <span className="max-w-[160px] truncate">{asset!.original_name}</span>
                      <button onClick={() => toggleAssetSelection(asset!.id)} className="rounded-full p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {assetsLoading ? (
                <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
                </div>
              ) : assets.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
                  <ImagePlus className="mx-auto h-12 w-12 text-slate-400" />
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">Your Brand Kit is empty</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">Upload your logo, packaging, product render, or branded artwork first. Then return here to drop those assets into AI-generated scenes.</p>
                  <button
                    onClick={() => navigate('/dashboard/assets')}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
                  >
                    Upload assets
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative mb-5">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search your Brand Kit assets"
                      value={assetSearch}
                      onChange={(event) => setAssetSearch(event.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-11 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:bg-white"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredAssets.length === 0 ? (
                      <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
                        No assets match your search.
                      </div>
                    ) : (
                      filteredAssets.map((asset) => {
                        const isSelected = selectedAssetIds.includes(asset.id);

                        return (
                          <button
                            key={asset.id}
                            onClick={() => toggleAssetSelection(asset.id)}
                            className={`group overflow-hidden rounded-3xl border text-left transition ${
                              isSelected
                                ? 'border-teal-500 bg-teal-50 shadow-md shadow-teal-100'
                                : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md'
                            }`}
                          >
                            <div className="relative aspect-[1.1/1] bg-slate-100">
                              <img src={asset.file_url} alt={asset.original_name} className="h-full w-full object-contain p-4" loading="lazy" />
                              <div className="absolute right-3 top-3 rounded-full bg-white/90 p-2 shadow-sm">
                                {isSelected ? <Check className="h-4 w-4 text-teal-700" /> : <Layers3 className="h-4 w-4 text-slate-400" />}
                              </div>
                            </div>
                            <div className="space-y-2 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <p className="line-clamp-2 text-sm font-semibold text-slate-900">{asset.original_name}</p>
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  {asset.file_type.split('/')[1] || 'image'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>{formatBytes(asset.file_size)}</span>
                                <span>{asset.width && asset.height ? `${asset.width} x ${asset.height}` : 'Flexible size'}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Step 2</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Describe the scene you want</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">Be specific about environment, lighting, camera angle, surface materials, and how the selected assets should feel in the composition.</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {PROMPT_STARTERS.map((starter) => (
                  <button
                    key={starter}
                    onClick={() => applyPromptStarter(starter)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                  >
                    {starter}
                  </button>
                ))}
              </div>

              <textarea
                value={primaryPrompt}
                onChange={(event) => setPrimaryPrompt(event.target.value)}
                placeholder="Example: Place the selected supplement bottle and box on a textured stone pedestal with warm side lighting, soft shadows, a premium wellness mood, and believable packaging detail."
                className="mt-5 min-h-[180px] w-full rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm leading-6 text-slate-800 outline-none transition focus:border-teal-500 focus:bg-white"
              />
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>Stronger prompts produce more controllable scenes.</span>
                <span>{primaryPrompt.trim().length} characters</span>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Step 3</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Shape the creative direction</h2>

              <div className="mt-5">
                <div className="mb-3 flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-800">Style modifiers</label>
                  <span className="text-xs text-slate-500">{selectedStyles.length} selected</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {STYLE_OPTIONS.map((style) => {
                    const isActive = selectedStyles.includes(style.value);
                    return (
                      <button
                        key={style.id}
                        onClick={() => toggleStyle(style.value)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                          isActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-800">Variations to generate</label>
                    <span className="text-xs text-slate-500">Up to 4 per run</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((count) => {
                      const isDisabled = count * 2 > creditsBalance;
                      return (
                        <button
                          key={count}
                          onClick={() => setVariationsCount(count)}
                          disabled={isDisabled}
                          className={`rounded-2xl border px-4 py-4 text-center transition ${
                            variationsCount === count
                              ? 'border-teal-500 bg-teal-50 text-teal-800'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                          } ${isDisabled ? 'cursor-not-allowed opacity-45' : ''}`}
                        >
                          <span className="block text-lg font-bold">{count}</span>
                          <span className="mt-1 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                            {count * 2} credits
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-800">Output ratio</label>
                    <span className="text-xs text-slate-500">{selectedAspect?.hint}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                    {ASPECT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAspectRatio(option.value)}
                        className={`rounded-2xl border px-4 py-4 text-left transition ${
                          aspectRatio === option.value
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                        <span className="mt-1 block text-xs text-slate-500">{option.value}</span>
                        <span className="mt-2 block text-xs text-slate-500">{option.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50">
                <button onClick={() => setShowAdvanced((current) => !current)} className="flex w-full items-center justify-between px-5 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-white p-2 shadow-sm">
                      <Settings2 className="h-4 w-4 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Advanced controls</p>
                      <p className="text-xs text-slate-500">Fine-tune what the model should avoid.</p>
                    </div>
                  </div>
                  {showAdvanced ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                </button>

                {showAdvanced && (
                  <div className="border-t border-slate-200 px-5 py-5">
                    <label className="mb-3 block text-sm font-semibold text-slate-800">Negative prompt</label>
                    <input
                      type="text"
                      value={negativePrompt}
                      onChange={(event) => setNegativePrompt(event.target.value)}
                      placeholder="Example: no people, no distorted text, no extra packaging, no unrealistic reflections"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500"
                    />
                    <p className="mt-3 text-xs leading-5 text-slate-500">Use this to suppress unwanted objects, clutter, or unreadable branding.</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Generate with the current plan</p>
                <p className="mt-1 text-sm text-slate-600">
                  {selectedAssetIds.length} asset{selectedAssetIds.length === 1 ? '' : 's'} selected, {variationsCount} variation{variationsCount === 1 ? '' : 's'}, {generationCost} credits total.
                </p>
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating || !hasEnoughCredits}
                className={`inline-flex items-center justify-center gap-3 rounded-2xl px-6 py-4 font-semibold transition ${
                  generating || !hasEnoughCredits
                    ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Generating variations...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5" />
                    Generate {variationsCount} scene variation{variationsCount === 1 ? '' : 's'}
                  </>
                )}
              </button>
            </div>
          </section>

          <aside className="space-y-6 xl:sticky xl:top-6 xl:self-start">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Generation Plan</p>
              <h3 className="mt-2 text-xl font-bold text-slate-900">What this run will produce</h3>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Assets</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {selectedAssetIds.length > 0 ? `${selectedAssetIds.length} selected` : 'No assets selected yet'}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    {selectedAssets.length > 0
                      ? selectedAssets.slice(0, 3).map((asset) => asset!.original_name).join(', ')
                      : 'Choose logos, product renders, packaging, or artwork from your Brand Kit.'}
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Scene direction</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {primaryPrompt.trim() ? 'Prompt ready' : 'Prompt needed'}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    {primaryPrompt.trim()
                      ? `${primaryPrompt.trim().slice(0, 140)}${primaryPrompt.trim().length > 140 ? '...' : ''}`
                      : 'Describe environment, lighting, and composition to guide the result.'}
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Variations</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{variationsCount}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Aspect Ratio</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{aspectRatio}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cost</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{generationCost}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Scene Tips</p>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-teal-700" />
                  Start with the hero asset first if one visual should dominate the composition.
                </li>
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-teal-700" />
                  Ask for camera angle, material texture, and lighting mood for more controllable outputs.
                </li>
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-teal-700" />
                  Results are automatically saved to your Library once generation completes.
                </li>
              </ul>
            </div>
          </aside>
        </div>

        {generatedMockups.length > 0 && (
          <section ref={resultsRef} className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">Results</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Your SceneScaper mockups are ready</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">Review each variation, open a larger preview, download what you need, or continue in Library.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleGenerate}
                  disabled={generating || !hasEnoughCredits}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    generating || !hasEnoughCredits
                      ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Regenerate set
                </button>
                <button
                  onClick={() => navigate('/dashboard/library')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <FolderOpen className="h-4 w-4" />
                  Open Library
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-2 2xl:grid-cols-4">
              {generatedMockups.map((mockup) => {
                const imageUrl = resolveSceneImageUrl(mockup.mockupUrl);
                const isDownloading = downloadingMockupId === mockup.mockupId;

                return (
                  <article key={mockup.mockupId} className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50">
                    <button onClick={() => setPreviewMockup(mockup)} className="group relative block aspect-[1/1] w-full overflow-hidden bg-slate-200">
                      <img src={imageUrl} alt={mockup.name} className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
                      <div className="absolute bottom-4 left-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900 opacity-0 shadow-sm transition group-hover:opacity-100">
                        Click to preview
                      </div>
                    </button>

                    <div className="space-y-4 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">{mockup.name}</h3>
                          <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">Variation {mockup.variationNumber}</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-700 shadow-sm">Ready</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setPreviewMockup(mockup)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleDownload(mockup)}
                          disabled={isDownloading}
                          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                            isDownloading ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-slate-900 text-white hover:bg-slate-800'
                          }`}
                        >
                          {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          Download
                        </button>
                      </div>

                      <button onClick={() => navigate(`/dashboard/library?preview=${mockup.mockupId}`)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 transition hover:text-slate-900">
                        Open in Library
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {previewMockup && (
        <ImagePreviewModal
          src={resolveSceneImageUrl(previewMockup.mockupUrl)}
          alt={previewMockup.name}
          title={previewMockup.name}
          subtitle={`Variation ${previewMockup.variationNumber}`}
          onClose={() => setPreviewMockup(null)}
          onDownload={() => {
            void handleDownload(previewMockup);
          }}
        />
      )}
    </div>
  );
};

export default SceneScaper;
